export interface ContextDataArgs {
  exception?: unknown;
  message: string;
  context?: Record<string, unknown>;
}

interface Attachment {
  filename: string;
  data: string;
  contentType: string;
}

export const processContextData = ({ message, exception, context = {} }: ContextDataArgs) => {
  const extra: Record<string, string> = { message };
  const attachments: Attachment[] = [];

  if (exception) {
    extra["exception"] = JSON.stringify(formatExceptionIfNeeded(exception));
  }

  for (const [property, value] of Object.entries(context)) {
    const { attachment, serializedValue } = serializeValue(property, value);

    if (attachment) {
      attachments.push(attachment);
    }

    extra[property] = serializedValue;
  }

  return { extra, attachments };
};

const serializeValue = (property: string, value: unknown): { attachment?: Attachment; serializedValue: string } => {
  try {
    // JSON.stringify returns undefined for these, despite its `string` lib typing
    if (value === undefined || typeof value === "function") {
      return { serializedValue: String(value) };
    }
    const serializedValue = JSON.stringify(value, (_key, nestedValue) => formatExceptionIfNeeded(nestedValue));

    if (serializedValue.length > ATTACHMENT_THRESHOLD) {
      return {
        attachment: { filename: `${property}.json`, data: serializedValue, contentType: "application/json" },
        serializedValue: `See attachment "${property}.json"`,
      };
    }
    return { attachment: undefined, serializedValue };
  } catch (serializationError) {
    return {
      serializedValue: `[Serialization Error: ${serializationError instanceof Error ? serializationError.message : "Unknown error"}]`,
    };
  }
};

const formatExceptionIfNeeded = (exception: unknown): unknown => {
  return exception instanceof Error
    ? {
        // oxlint-disable-next-line typescript/no-misused-spread -- Intentionally copy enumerable diagnostic fields only.
        ...exception,
        stack: exception.stack,
        cause: formatExceptionIfNeeded(exception.cause),
        name: exception.name,
        message: exception.message,
      }
    : exception;
};

const ATTACHMENT_THRESHOLD = 5000;
